import React, { useState, useId } from 'react';
import { useAuth } from '../hooks/useAuth';

/* ── tiny shared sub-components ────────────────────────── */
const inputStyle = {
  width: '100%',
  padding: '0.6rem 0.8rem',
  borderRadius: '0.5rem',
  border: '1.5px solid #fcd34d',
  outline: 'none',
  fontSize: '0.85rem',
  color: '#78350f',
  background: '#fffbeb',
  fontFamily: 'inherit',
  boxSizing: 'border-box',
};

const BilingualLabel = ({ en, hi, required }) => (
  <span style={{ display: 'block', marginBottom: '0.25rem' }}>
    <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#92400e' }}>
      {en}
      {required && <span style={{ color: '#dc2626' }}> *</span>}
    </span>
    {hi ? (
      <span style={{ display: 'block', fontSize: '0.68rem', fontWeight: 500, color: '#a16207', marginTop: '0.1rem' }}>
        {hi}
      </span>
    ) : null}
  </span>
);

function Field({ label, labelHi, type, value, onChange, placeholder, required, disabled, fullWidth }) {
  const id = useId();
  return (
    <div style={{ marginBottom: '0.8rem', ...(fullWidth ? { gridColumn: '1 / -1' } : {}) }}>
      <label htmlFor={id} style={{ display: 'block', cursor: disabled ? 'default' : 'pointer' }}>
        <BilingualLabel en={label} hi={labelHi} required={required} />
      </label>
      <input
        id={id}
        type={type} value={value} onChange={onChange}
        placeholder={placeholder} required={required} disabled={disabled}
        style={{ ...inputStyle, opacity: disabled ? 0.7 : 1 }}
        onFocus={e => e.target.style.borderColor='#f59e0b'}
        onBlur={e => e.target.style.borderColor='#fcd34d'}
      />
    </div>
  );
}

const PrimaryBtn = ({ children, loading, style = {} }) => (
  <button type="submit" disabled={loading}
    style={{ width:'100%', padding:'0.7rem', borderRadius:'0.6rem', border:'none',
      fontWeight:700, fontSize:'0.88rem', cursor: loading ? 'not-allowed' : 'pointer',
      opacity: loading ? 0.65 : 1, transition:'all .2s', ...style }}>
    {loading ? '⏳ Please wait... / Kripya wait karein...' : children}
  </button>
);

const Err = ({ msg }) => msg ? (
  <div style={{ background:'#fef2f2', border:'1px solid #fca5a5', borderRadius:'0.5rem',
    padding:'0.5rem 0.8rem', marginBottom:'0.75rem', fontSize:'0.78rem', color:'#dc2626' }}>
    {msg}
  </div>
) : null;

const Ok = ({ msg }) => msg ? (
  <div style={{ background:'#f0fdf4', border:'1px solid #86efac', borderRadius:'0.5rem',
    padding:'0.5rem 0.8rem', marginBottom:'0.75rem', fontSize:'0.78rem', color:'#166534' }}>
    {msg}
  </div>
) : null;

/* ── main component ─────────────────────────────────────── */
export default function LoginModal({ isOpen, onClose, onSuccess, inline = false }) {
  const { signIn, signUp, requestPasswordReset, verifyPasswordResetOtp, resetPasswordWithOtp } = useAuth();

  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [loginErr, setLoginErr]   = useState('');
  const [loginLoading, setLoginLoading] = useState(false);

  // Forgot password — lightweight 3-step stepper within the login column:
  // 1 = enter email, 2 = enter OTP, 3 = enter new password, 4 = done
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotStep, setForgotStep]       = useState(1);
  const [forgotEmail, setForgotEmail]     = useState('');
  const [forgotOtp, setForgotOtp]         = useState('');
  const [forgotPassword, setForgotPassword] = useState('');
  const [forgotConfirm, setForgotConfirm] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotMsg, setForgotMsg]         = useState('');
  const [forgotErr, setForgotErr]         = useState('');

  const resetForgotFlow = () => {
    setShowForgotPassword(false);
    setForgotStep(1);
    setForgotOtp('');
    setForgotPassword('');
    setForgotConfirm('');
    setForgotErr('');
    setForgotMsg('');
  };

  // signup — auth-only; birth/profile details are collected later during profile creation
  const [signupForm, setSignupForm] = useState({
    email: '',
    password: '',
    confirm: '',
  });
  const [signupErr, setSignupErr]   = useState('');
  const [signupOk, setSignupOk]     = useState('');
  const [signupLoading, setSignupLoading] = useState(false);

  if (!isOpen && !inline) return null;

  /* ─── handlers ─── */
  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    setLoginErr('');
    if (!loginForm.email || !loginForm.password) { setLoginErr('Please fill both fields / Dono fields bharen'); return; }
    setLoginLoading(true);
    const { error } = await signIn(loginForm.email, loginForm.password);
    setLoginLoading(false);
    if (error) { setLoginErr(error.message || 'Invalid email or password'); return; }
    onClose(); onSuccess();
  };

  const handleSendResetOtp = async (e) => {
    e.preventDefault();
    setForgotErr(''); setForgotMsg('');
    if (!forgotEmail) { setForgotErr('Please enter your email / Email daalen'); return; }
    setForgotLoading(true);
    const { error } = await requestPasswordReset(forgotEmail);
    setForgotLoading(false);
    if (error) { setForgotErr(error.message || 'Failed to send reset code'); return; }
    setForgotStep(2);
    setForgotMsg(`Code sent to ${forgotEmail}`);
  };

  const handleVerifyResetOtp = async (e) => {
    e.preventDefault();
    setForgotErr('');
    if (!forgotOtp || forgotOtp.length !== 6) { setForgotErr('Enter the 6-digit code / 6-digit code daalen'); return; }
    setForgotLoading(true);
    const { error } = await verifyPasswordResetOtp(forgotEmail, forgotOtp);
    setForgotLoading(false);
    if (error) { setForgotErr(error.message || 'Invalid code'); return; }
    setForgotMsg('');
    setForgotStep(3);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setForgotErr('');
    if (forgotPassword.length < 8) { setForgotErr('Password must be at least 8 characters'); return; }
    if (forgotPassword !== forgotConfirm) { setForgotErr('Passwords do not match / Password match nahi ho rahe'); return; }
    setForgotLoading(true);
    const { error } = await resetPasswordWithOtp(forgotEmail, forgotOtp, forgotPassword);
    setForgotLoading(false);
    if (error) { setForgotErr(error.message || 'Failed to reset password'); return; }
    setForgotStep(4);
    setForgotMsg('✅ Password updated! You can now log in.');
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setSignupErr(''); setSignupOk('');
    if (!signupForm.email?.trim()) { setSignupErr('Email is required / Email zaroori hai'); return; }
    if (!signupForm.password) { setSignupErr('Password is required / Password zaroori hai'); return; }
    if (signupForm.password.length < 8) { setSignupErr('Password must be at least 8 characters'); return; }
    if (signupForm.password !== signupForm.confirm) { setSignupErr('Passwords do not match / Password match nahi ho rahe'); return; }

    setSignupLoading(true);
    const { data, error } = await signUp({
      email: signupForm.email.trim(),
      password: signupForm.password,
    });
    setSignupLoading(false);
    if (error) { setSignupErr(error.message || 'Signup failed'); return; }
    if (data) {
      setSignupOk('✅ Account created! You are now logged in.');
      setTimeout(() => { onClose(); onSuccess(); }, 900);
    }
  };

  const wrapperStyle = inline
    ? { fontFamily:"system-ui,-apple-system,sans-serif", width:'100%' }
    : {
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 9999,
        width: 'min(960px, 98vw)',
        borderRadius: '16px',
        overflow: 'hidden',
        boxShadow: '0 8px 30px rgba(0,0,0,0.06), 0 32px 64px rgba(0,0,0,0.12)',
        fontFamily: 'system-ui,-apple-system,sans-serif',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
      };

  return (
    <>
      <style>{`
        .ks-login-modal-columns { display: grid; grid-template-columns: 1fr; align-items: start; }
        @media (min-width: 720px) {
          /* Equal columns: login inputs match create-account full-width field width */
          .ks-login-modal-columns { grid-template-columns: minmax(280px, 1fr) minmax(280px, 1fr); }
          .ks-login-modal-login,
          .ks-login-modal-signup { order: unset; }
        }
        /* Mobile / narrow: Create Account first, Login below — DOM stays login-first for desktop columns */
        @media (max-width: 719px) {
          .ks-login-modal-signup {
            order: 1;
            border-bottom: 1px solid #fde68a;
          }
          .ks-login-modal-login {
            order: 2;
            border-right: none !important;
            border-bottom: none !important;
            position: relative !important;
          }
        }
        @media (min-width: 520px) {
          .ks-signup-form-grid { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
        }
      `}</style>
      {!inline && (
        <div onClick={onClose}
          className="ks-modal-scrim"
          style={{ position:'fixed', inset:0, zIndex:9998 }} />
      )}

      <div style={wrapperStyle}>
        {/* Header */}
        <div style={{ background:'linear-gradient(135deg,#78350f,#b45309)', padding:'1rem 1.5rem', display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink: 0, gap:'0.75rem', flexWrap:'wrap' }}>
          <div style={{ display:'flex', alignItems:'center', gap:'0.6rem' }}>
            <span style={{ fontSize:'1.3rem' }}>🪐</span>
            <span style={{ color:'#fff', fontWeight:800, fontSize:'clamp(0.82rem, 2vw, 1rem)', lineHeight:1.35 }}>Kundli Shastra — Login / Sign Up</span>
          </div>
          {!inline && (
            <button onClick={onClose}
              style={{ background:'rgba(255,255,255,.2)', border:'none', color:'#fff', width:28, height:28, borderRadius:'50%', cursor:'pointer', fontSize:17, display:'flex', alignItems:'center', justifyContent:'center' }}>
              ×
            </button>
          )}
        </div>

        {/* Two equal columns — scroll body; same usable width for login inputs & signup full-width rows */}
        <div
          className="ks-login-modal-columns"
          style={{
            background: 'linear-gradient(rgb(255, 247, 237), rgb(255, 251, 235))',
            ...(inline ? {} : { flex: 1, minHeight: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch' }),
          }}
        >

          {/* ── LEFT: LOGIN ── */}
          <div
            className="ks-login-modal-login"
            style={{
              padding: '1.25rem 1.35rem',
              borderRight: '1px solid #fde68a',
              position: 'sticky',
              top: 0,
              alignSelf: 'start',
              maxHeight: '100%',
            }}
          >
            <p style={{ fontSize:'0.68rem', fontWeight:700, letterSpacing:'.09em', textTransform:'uppercase', color:'#b45309', margin:'0 0 0.2rem' }}>Already have an account</p>
            <h3 style={{ fontSize:'1.2rem', fontWeight:800, color:'#78350f', margin:'0 0 0.15rem' }}>Login</h3>
            <p style={{ fontSize:'0.72rem', fontWeight:600, color:'#a16207', margin:'0 0 1rem' }}>Log in</p>

            {!showForgotPassword ? (
              <>
                <form onSubmit={handlePasswordLogin}>
                  <Err msg={loginErr} />
                  <Field label="Email" labelHi="Email (e.g. user@gmail.com)" type="email" value={loginForm.email} onChange={e => setLoginForm(p => ({...p, email:e.target.value}))} placeholder="your@email.com" required />
                  <Field label="Password" labelHi="Password" type="password" value={loginForm.password} onChange={e => setLoginForm(p => ({...p, password:e.target.value}))} placeholder="Your password" required />
                  <PrimaryBtn loading={loginLoading} style={{ background:'linear-gradient(135deg,#d97706,#b45309)', color:'#fff', marginTop:'0.25rem' }}>
                    Login →
                  </PrimaryBtn>
                </form>
                <button
                  type="button"
                  onClick={() => { setShowForgotPassword(true); setForgotStep(1); setForgotEmail(loginForm.email); setForgotErr(''); setForgotMsg(''); }}
                  style={{ width:'100%', background:'transparent', border:'none', color:'#b45309', fontSize:'0.78rem', marginTop:'0.6rem', cursor:'pointer', textDecoration:'underline', textAlign:'center' }}
                >
                  Forgot Password?
                </button>
              </>
            ) : (
              <div>
                {/* Step progress */}
                {forgotStep < 4 && (
                  <div style={{ display:'flex', gap:'0.3rem', marginBottom:'0.9rem' }}>
                    {[1, 2, 3].map((s) => (
                      <div key={s} style={{ flex:1, height:3, borderRadius:999, background: s <= forgotStep ? '#d97706' : '#fde68a' }} />
                    ))}
                  </div>
                )}

                {forgotStep === 1 && (
                  <form onSubmit={handleSendResetOtp}>
                    <p style={{ fontSize:'0.78rem', color:'#78350f', margin:'0 0 0.75rem', lineHeight:1.5 }}>
                      Enter your email and we'll send you a reset code.
                    </p>
                    <Err msg={forgotErr} />
                    <Field label="Email" labelHi="Email (e.g. user@gmail.com)" type="email" value={forgotEmail} onChange={e => setForgotEmail(e.target.value)} placeholder="your@email.com" required />
                    <PrimaryBtn loading={forgotLoading} style={{ background:'linear-gradient(135deg,#d97706,#b45309)', color:'#fff', marginTop:'0.25rem' }}>
                      Send Reset Code →
                    </PrimaryBtn>
                  </form>
                )}

                {forgotStep === 2 && (
                  <form onSubmit={handleVerifyResetOtp}>
                    <Err msg={forgotErr} />
                    <Ok msg={forgotMsg} />
                    <Field label="6-digit code" labelHi="Check your email" type="text" value={forgotOtp} onChange={e => setForgotOtp(e.target.value.replace(/\D/g,'').slice(0,6))} placeholder="000000" required />
                    <PrimaryBtn loading={forgotLoading} style={{ background:'linear-gradient(135deg,#d97706,#b45309)', color:'#fff', marginTop:'0.25rem' }}>
                      Verify Code →
                    </PrimaryBtn>
                    <button
                      type="button"
                      onClick={() => { setForgotStep(1); setForgotOtp(''); setForgotErr(''); setForgotMsg(''); }}
                      style={{ width:'100%', background:'transparent', border:'none', color:'#b45309', fontSize:'0.78rem', marginTop:'0.4rem', cursor:'pointer' }}
                    >
                      ← Resend code
                    </button>
                  </form>
                )}

                {forgotStep === 3 && (
                  <form onSubmit={handleResetPassword}>
                    <p style={{ fontSize:'0.78rem', color:'#78350f', margin:'0 0 0.75rem', lineHeight:1.5 }}>
                      Choose a new password for your account.
                    </p>
                    <Err msg={forgotErr} />
                    <Field label="New Password" labelHi="Min. 8 characters" type="password" value={forgotPassword} onChange={e => setForgotPassword(e.target.value)} placeholder="Min. 8 characters" required />
                    <Field label="Confirm Password" labelHi="Repeat password" type="password" value={forgotConfirm} onChange={e => setForgotConfirm(e.target.value)} placeholder="Repeat password" required />
                    <PrimaryBtn loading={forgotLoading} style={{ background:'linear-gradient(135deg,#78350f,#92400e)', color:'#fff', marginTop:'0.25rem' }}>
                      Update Password →
                    </PrimaryBtn>
                  </form>
                )}

                {forgotStep === 4 && (
                  <div>
                    <Ok msg={forgotMsg} />
                    <button
                      type="button"
                      onClick={resetForgotFlow}
                      style={{ width:'100%', padding:'0.7rem', borderRadius:'0.6rem', border:'none', fontWeight:700, fontSize:'0.88rem', cursor:'pointer', background:'linear-gradient(135deg,#d97706,#b45309)', color:'#fff' }}
                    >
                      Back to Login →
                    </button>
                  </div>
                )}

                {forgotStep < 4 && (
                  <button
                    type="button"
                    onClick={resetForgotFlow}
                    style={{ width:'100%', background:'transparent', border:'none', color:'#b45309', fontSize:'0.78rem', marginTop:'0.6rem', cursor:'pointer' }}
                  >
                    ← Back to login
                  </button>
                )}
              </div>
            )}

            <p style={{ fontSize:'0.68rem', color:'#b45309', textAlign:'center', marginTop:'0.85rem', lineHeight:1.55 }}>
              Save your kundli charts and access them anytime
            </p>
          </div>

          {/* ── RIGHT: SIGNUP (first on mobile via CSS order) ── */}
          <div className="ks-login-modal-signup" style={{ padding:'1.25rem 1.35rem', background:'linear-gradient(180deg,#fff7ed,#fffbeb)' }}>
            <p style={{ fontSize:'0.68rem', fontWeight:700, letterSpacing:'.09em', textTransform:'uppercase', color:'#b45309', margin:'0 0 0.2rem' }}>New here?</p>
            <h3 style={{ fontSize:'1.2rem', fontWeight:800, color:'#78350f', margin:'0 0 0.15rem' }}>Create Account</h3>
            <p style={{ fontSize:'0.72rem', fontWeight:600, color:'#a16207', margin:'0 0 0.85rem' }}>Create your new account</p>

            <form
              className="ks-signup-form-grid"
              onSubmit={handleSignup}
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr',
                columnGap: '0.85rem',
                alignItems: 'start',
              }}
            >
              <div style={{ gridColumn: '1 / -1' }}><Err msg={signupErr} /></div>
              <div style={{ gridColumn: '1 / -1' }}><Ok msg={signupOk} /></div>
              <Field fullWidth label="Email" labelHi="Email (e.g. user@gmail.com)" type="email" value={signupForm.email} onChange={e => setSignupForm(p => ({...p, email:e.target.value}))} placeholder="your@email.com" required />
              <Field label="Password" labelHi="Password" type="password" value={signupForm.password} onChange={e => setSignupForm(p => ({...p, password:e.target.value}))} placeholder="Min. 8 characters" required />
              <Field label="Confirm Password" labelHi="Confirm password" type="password" value={signupForm.confirm} onChange={e => setSignupForm(p => ({...p, confirm:e.target.value}))} placeholder="Repeat password" required />
              <PrimaryBtn loading={signupLoading} style={{ background:'linear-gradient(135deg,#78350f,#92400e)', color:'#fff', marginTop:'0.15rem', gridColumn: '1 / -1' }}>
                Create Account →
              </PrimaryBtn>
            </form>

            <p style={{ fontSize:'0.66rem', color:'#b45309', textAlign:'center', marginTop:'0.75rem', lineHeight:1.55 }}>
              🔒 Passwords are securely hashed · Your data is private
            </p>
            <p style={{ fontSize:'0.68rem', color:'#a16207', textAlign:'center', marginTop:'0.4rem', lineHeight:1.5 }}>
              You'll set up your birth details right after creating your account
            </p>
          </div>

        </div>
      </div>
    </>
  );
}
